import type {
  BetMove,
  ShowMeTheCoinAction,
} from "@cloudnest/show-me-the-coin-engine";
import { isPlayerId } from "@cloudnest/show-me-the-coin-engine";

/**
 * Typed replay-event adapter for 쇼 미 더 코인 (show-me-the-coin).
 *
 * Hidden-information game: the persisted `init` event carries the full
 * authoritative state (both players' coins); the replay runner masks it for
 * display. Player events are `SubmitCoins` and `Bet`. Malformed events fail
 * loudly (unknown type / playerId / move) rather than being silently coerced.
 */
export type ShowMeTheCoinReplayEventType = "init" | "SubmitCoins" | "Bet";

export const SHOW_ME_THE_COIN_REPLAY_EVENT_TYPES = [
  "init",
  "SubmitCoins",
  "Bet",
] as const satisfies readonly ShowMeTheCoinReplayEventType[];

const BET_MOVES: readonly BetMove[] = ["check", "bet", "call", "raise", "fold"];

export function normalizeShowMeTheCoinReplayEventType(
  eventType: string,
): ShowMeTheCoinReplayEventType {
  if (
    (SHOW_ME_THE_COIN_REPLAY_EVENT_TYPES as readonly string[]).includes(
      eventType,
    )
  ) {
    return eventType as ShowMeTheCoinReplayEventType;
  }
  throw new Error(`Unsupported show-me-the-coin event type: ${eventType}`);
}

function asPlayerId(value: unknown) {
  if (isPlayerId(value)) return value;
  throw new Error(
    `Invalid show-me-the-coin playerId in event payload: ${value}`,
  );
}

function asBetMove(value: unknown): BetMove {
  if (typeof value === "string" && (BET_MOVES as string[]).includes(value)) {
    return value as BetMove;
  }
  throw new Error(
    `Invalid show-me-the-coin bet move in event payload: ${value}`,
  );
}

export function showMeTheCoinEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): ShowMeTheCoinAction {
  const type = normalizeShowMeTheCoinReplayEventType(eventType);
  if (type === "init") {
    throw new Error("showMeTheCoinEventToAction cannot convert the init event");
  }
  const playerId = asPlayerId(payload.playerId);
  if (type === "SubmitCoins") {
    if (!Array.isArray(payload.coins)) {
      throw new Error("Invalid show-me-the-coin SubmitCoins coins in payload");
    }
    return {
      type: "SubmitCoins",
      playerId,
      coins: payload.coins.map((coin) => {
        if (typeof coin !== "number") {
          throw new Error("show-me-the-coin coin must be a number");
        }
        return coin;
      }),
    };
  }
  return { type: "Bet", playerId, move: asBetMove(payload.move) };
}
